import { createFileRoute } from "@tanstack/react-router";
import { Clock, ExternalLink, MapPin, Share2, Ticket } from "lucide-react";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { translateDiscoveryCategory } from "@pulse/shared";
import { AiImageBadge } from "@/components/AiImageBadge";
import { CardInterestButton } from "@/components/CardInterestButton";
import { DetailBackButton, useDetailBack } from "@/components/DetailBackButton";
import {
  ProposeToSquadSheet,
} from "@/components/squad/ProposeToSquadSheet";
import { EventCard } from "@/components/EventCard";
import { EventImage } from "@/components/EventImage";
import { WeatherWidget } from "@/components/WeatherWidget";
import { LoadingBlock, ErrorBlock } from "@/components/AsyncState";
import { StarDisplay } from "@/components/ratings/RatingWidget";
import { EntityRatingSection } from "@/components/ratings/EntityRatingSection";
import { useShareVenue } from "@/hooks/useShareVenue";
import { detailHeroViewTransitionStyle } from "@/lib/depthPushTransition";
import { zurichDateKey } from "@/lib/eventSchedule";
import { useVenue, useVenueEvents, useVenueInterestedCount } from "@/lib/events";
import { CONSUMER_TIER_LABELS } from "@/lib/consumerTier";
import { useConsumerEntitlements } from "@/lib/consumerEntitlements";
import { isOutdoorCategory } from "@/mocks/event.mocks";
import { formatDistanceKm, haversineKm, isCityCenterFallbackCoords } from "@/lib/geo";
import { useUserInterestedEvents, useUserInterestedVenues } from "@/lib/interested";
import {
  getTodayClosingMinutes,
  getTodayOpeningMinutes,
  getVenueHoursDisplay,
  getVenueOpenStatus,
  minutesToTime24,
  useVenueHoursTick,
} from "@/lib/venueHours";
import {
  getDresscodeForCategory,
  getVenueDescription,
  getVenueDisplayName,
  googleMapsNavigationUrlForVenue,
  venueCardTitle,
} from "@/lib/venueDisplay";
import { useVenueHasTicketTypes } from "@/lib/venueTicketing";
import { cn } from "@/lib/utils";
import { ReportWrongDataButton } from "@/components/FeedbackSheet";
import { trackConsumerGeoActivity } from "@/lib/geoActivityTrack";

const venueIconButtonClass =
  "grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 bg-black/40 text-pulse backdrop-blur-sm transition-colors hover:bg-black/55 active:scale-95";

type VenueLocationMapProps = {
  lat: number;
  lng: number;
  label: string;
  className?: string;
};

export const Route = createFileRoute("/venue/$id")({
  head: () => ({
    meta: [
      { title: "Venue · Pulse" },
      { name: "description", content: "Venue details and upcoming events." },
    ],
  }),
  component: VenueDetail,
});

function CardDivider() {
  return <div className="my-4 border-t border-border" role="separator" />;
}

function formatStatTime(minutes: number | null): string {
  if (minutes == null) return "—";
  if (minutes === 0) return "00:00";
  return minutesToTime24(minutes);
}

function buildVenueAboutBlurb(
  t: TFunction<"consumer">,
  category: string,
  city: string,
  todayHours: string,
  address: string | null,
): string {
  const parts: string[] = [t("venue.blurbCategoryCity", { category, city })];
  if (todayHours && todayHours !== "—") {
    parts.push(t("venue.blurbOpen", { hours: todayHours }));
  }
  if (address) parts.push(address);
  return parts.join(" · ");
}

function displayRating(pulseRating: number | null, googleRating: number | null): number | null {
  if (pulseRating != null && pulseRating > 0) return pulseRating;
  if (googleRating != null && googleRating > 0) return googleRating;
  return null;
}

function VenueDetail() {
  const { id } = Route.useParams();
  const { t } = useTranslation("consumer");
  const goBack = useDetailBack();
  const { data: venue, isLoading, isError, error, refetch } = useVenue(id);
  const { entitlements } = useConsumerEntitlements();
  const { data: rawUpcomingEvents = [], isLoading: eventsLoading } = useVenueEvents(id);
  // Phase 5: get_upcoming_events_for_venue applies staffelung; keep teasers for unlock UX.
  const upcomingEvents = rawUpcomingEvents;
  const discoveryHiddenCount = useMemo(
    () => rawUpcomingEvents.filter((e) => e.isTeaser).length,
    [rawUpcomingEvents],
  );
  const { data: interestedCount = 0 } = useVenueInterestedCount(id);
  const { data: hasTicketTypes = false } = useVenueHasTicketTypes(id);
  const upcomingEventIds = useMemo(() => upcomingEvents.map((e) => e.id), [upcomingEvents]);
  const { data: interestedSet } = useUserInterestedVenues([id]);
  const { data: interestedEventSet } = useUserInterestedEvents(upcomingEventIds);
  const { execute: shareVenue, message: shareStatus } = useShareVenue(venue);
  const [VenueLocationMap, setVenueLocationMap] = useState<ComponentType<VenueLocationMapProps> | null>(
    null,
  );
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  const now = useVenueHoursTick();

  useEffect(() => {
    import("@/components/VenueLocationMap").then((m) => {
      setVenueLocationMap(() => m.VenueLocationMap);
    });
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
  }, []);

  const distanceLabel = useMemo(() => {
    if (!venue?.lat || !venue.lng) return venue?.distance ?? null;
    if (userCoords) {
      const km = haversineKm(userCoords.lat, userCoords.lng, venue.lat, venue.lng);
      return formatDistanceKm(km);
    }
    return venue.distance;
  }, [venue, userCoords]);

  const mapsUrl = useMemo(() => {
    if (!venue) return "#";
    const target = {
      lat: venue.lat,
      lng: venue.lng,
      name: venue.name,
      city: venue.city,
      address: venue.address,
    };
    if (userCoords) {
      return googleMapsNavigationUrlForVenue(target, userCoords);
    }
    return googleMapsNavigationUrlForVenue(target);
  }, [venue, userCoords]);

  const galleryImages = useMemo(() => {
    if (!venue) return [];
    const urls = [venue.image, ...(venue.imageAlternates ?? [])].filter(Boolean);
    return [...new Set(urls)];
  }, [venue]);

  useEffect(() => {
    if (!venue?.id) return;
    trackConsumerGeoActivity("venue_view");
  }, [venue?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <LoadingBlock label={t("venue.loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-5 pt-20">
        <ErrorBlock
          message={error instanceof Error ? error.message : t("venue.loadError")}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="px-5 pt-20 text-center">
        <p className="text-sm font-semibold">{t("venue.notFound")}</p>
        <button type="button" onClick={goBack} className="mt-4 inline-block text-sm text-pulse">
          {t("venue.backToHome")}
        </button>
      </div>
    );
  }

  const status = getVenueOpenStatus(venue.opening_hours, venue.category, now, venue.name);
  const todayHoursLabel =
    getVenueHoursDisplay(venue.opening_hours, venue.category, now, venue.name) || "—";
  const openingMinutes = getTodayOpeningMinutes(venue.opening_hours, venue.category, now, venue.name);
  const closingMinutes = getTodayClosingMinutes(venue.opening_hours, venue.category, now, venue.name);
  const dresscode = getDresscodeForCategory(venue.category);
  const rating = displayRating(venue.pulseRating, venue.googleRating);
  const categoryLabel = translateDiscoveryCategory(venue.category, t);
  const aboutText =
    getVenueDescription(venue) ??
    buildVenueAboutBlurb(t, categoryLabel, venue.city, todayHoursLabel, venue.address);
  const displayLabel = getVenueDisplayName(venue);
  const cardTitle = venueCardTitle(venue);
  const hasCoords = venue.lat != null && venue.lng != null;
  const showPreciseMap =
    hasCoords && !isCityCenterFallbackCoords(venue.lat, venue.lng, venue.city);
  const isOutdoor = isOutdoorCategory(venue.category);
  const statusLabel =
    status === "open"
      ? t("venue.status.open")
      : status === "opens-later"
        ? t("venue.status.opensLater")
        : t("venue.status.closed");

  const handleTicketClick = () => {
    toast.info(t("event.ticketingComingSoon"));
  };

  return (
    <div className="pulse-detail-body-fade min-h-screen bg-background pb-[calc(env(safe-area-inset-bottom,0px)+11.5rem)]">
      <div className="venue-detail-hero" style={detailHeroViewTransitionStyle()}>
        <EventImage
          src={venue.image}
          alt={displayLabel}
          className="venue-detail-hero__image"
          preset="hero"
          fetchPriority="high"
        />
        {venue.image ? <AiImageBadge /> : null}
        <div className="venue-detail-hero__top-fade" aria-hidden />
        <div className="venue-detail-hero__bottom-fade" aria-hidden />

        <div className="venue-detail-hero__nav">
          <DetailBackButton
            className="venue-detail-hero__icon-btn"
            aria-label={t("actions.back", { ns: "common" })}
          />
          <div className="venue-detail-hero__nav-actions">
            <ReportWrongDataButton
              defaultCategory="wrong_venue_info"
              context={{ venue_id: venue.id, path: `/venue/${venue.id}` }}
              className="venue-detail-hero__icon-btn"
            />
            <button
              type="button"
              onClick={() => void shareVenue()}
              aria-label={t("actions.share", { ns: "common" })}
              className="venue-detail-hero__icon-btn"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <CardInterestButton
              targetId={venue.id}
              isInterested={interestedSet?.has(venue.id) ?? false}
              targetType="venue"
              className="venue-detail-hero__icon-btn !h-[38px] !w-[38px] !border-white/14 !bg-[rgba(8,8,10,0.45)] !text-[var(--vc-platinum,#edeef1)]"
            />
          </div>
        </div>

        <div className="venue-detail-hero__badges">
          {status === "open" && (
            <span className="venue-detail-hero__badge venue-detail-hero__badge--live">
              <span className="venue-detail-hero__badge-dot venue-detail-hero__badge-dot--pulse" aria-hidden />
              {statusLabel}
            </span>
          )}
          {venue.vibe && (
            <span className="venue-detail-hero__badge venue-detail-hero__badge--live">
              {t(`venue.vibe.${venue.vibe}`)}
            </span>
          )}
          <span className="venue-detail-hero__badge">{categoryLabel}</span>
        </div>

        <div className="venue-detail-hero__content">
          <p className="venue-detail-hero__cat">{categoryLabel}</p>
          <h1 className="venue-detail-hero__title">{cardTitle}</h1>
          <div className="venue-detail-hero__meta">
            {rating != null && (
              <span className="venue-detail-hero__stars inline-flex items-center gap-1.5">
                <StarDisplay value={rating} size="sm" />
                <span>{rating.toFixed(1)}</span>
              </span>
            )}
            {rating != null && distanceLabel && (
              <span className="venue-detail-hero__meta-sep" aria-hidden>
                ·
              </span>
            )}
            {distanceLabel && <span>{distanceLabel}</span>}
          </div>
        </div>
      </div>

      <div className="venue-detail-quick">
        <div className="venue-detail-chip">
          <span className="venue-detail-chip__lbl">{t("venue.statusLabel")}</span>
          <span className="venue-detail-chip__val">{statusLabel}</span>
        </div>
        {dresscode && (
          <div className="venue-detail-chip">
            <span className="venue-detail-chip__lbl">{t("venue.dressCode")}</span>
            <span className="venue-detail-chip__val">{dresscode}</span>
          </div>
        )}
        {rating != null && (
          <div className="venue-detail-chip">
            <span className="venue-detail-chip__lbl">{t("venue.rating")}</span>
            <span className="venue-detail-chip__val">{rating.toFixed(1)}</span>
          </div>
        )}
      </div>

      {interestedCount > 0 && (
        <p className="venue-detail-crowd">
          {interestedCount} {t("venue.interestedCrowd")}
        </p>
      )}

      <div className="mt-2 space-y-6 px-5">
        <div className="flex flex-col rounded-3xl border border-border bg-surface p-5 shadow-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold">{cardTitle}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{todayHoursLabel}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {venue.website && (
                <a
                  href={venue.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("venue.website")}
                  className={venueIconButtonClass}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("venue.mapsLink")}
                className={venueIconButtonClass}
              >
                <MapPin className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div className="rounded-2xl border border-border bg-surface-elevated p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t("venue.opens")}
              </p>
              <p className="mt-1 inline-flex items-center gap-1 font-semibold">
                <Clock className="h-3.5 w-3.5" />
                {formatStatTime(openingMinutes)}
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-surface-elevated p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t("venue.closes")}
              </p>
              <p className="mt-1 inline-flex items-center gap-1 font-semibold">
                <Clock className="h-3.5 w-3.5" />
                {formatStatTime(closingMinutes)}
              </p>
            </div>
          </div>

          <CardDivider />

          <div>
            <h2 className="text-sm font-bold">{t("venue.about")}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{aboutText}</p>
          </div>

          {(venue.address || distanceLabel) && (
            <>
              <CardDivider />
              <div>
                <h2 className="text-sm font-bold">{t("venue.address")}</h2>
                {venue.address && (
                  <p className="mt-1.5 text-sm text-muted-foreground">{venue.address}</p>
                )}
                {distanceLabel && (
                  <p className={cn("text-sm text-muted-foreground", venue.address && "mt-1")}>
                    {t("event.distanceAway", { distance: distanceLabel })}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {isOutdoor && hasCoords && (
          <section className="rounded-2xl border border-border bg-surface p-4">
            <h2 className="text-lg font-bold">{t("event.liveVibeWeather")}</h2>
            <div className="mt-3">
              <WeatherWidget lat={venue.lat} lng={venue.lng} enabled embedded />
            </div>
          </section>
        )}

        <section>
          <h2 className="text-lg font-bold">{t("venue.upcomingEvents")}</h2>
          {discoveryHiddenCount > 0 && entitlements?.eventVisibilityHours != null && (
            <p className="mt-3 rounded-2xl border border-pulse/20 bg-pulse/5 px-4 py-3 text-xs text-muted-foreground">
              {t("entitlements.discoveryHidden", {
                count: discoveryHiddenCount,
                tier:
                  CONSUMER_TIER_LABELS[
                    entitlements.eventVisibilityHours <= 48 ? "circle_plus" : "elite"
                  ],
              })}
            </p>
          )}
          {eventsLoading ? (
            <div className="mt-4">
              <LoadingBlock label={t("venue.loadingEvents")} />
            </div>
          ) : upcomingEvents.length === 0 ? (
            <p className="mt-3 rounded-2xl border border-border bg-surface p-6 text-center text-sm text-muted-foreground">
              {t("venue.noEvents")}
            </p>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {upcomingEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  isInterested={interestedEventSet?.has(event.id) ?? false}
                />
              ))}
            </div>
          )}
        </section>

        {galleryImages.length > 1 && (
          <section>
            <h2
              className="mb-2.5"
              style={{
                fontFamily: "var(--vc-font-display, Fraunces, serif)",
                fontWeight: 500,
                fontSize: 17,
              }}
            >
              {t("event.impressions")}
            </h2>
            <div className="venue-detail-gallery !px-0">
              {galleryImages.slice(1).map((src, i) => (
                <div key={`${src}-${i}`} className="relative shrink-0">
                  <EventImage
                    src={src}
                    alt=""
                    className="venue-detail-gallery__item"
                    preset="thumb"
                  />
                  <AiImageBadge size="sm" />
                </div>
              ))}
            </div>
          </section>
        )}

        {showPreciseMap && (
          <section>
            <h2 className="text-lg font-bold">{t("event.map")}</h2>
            <div className="mt-4">
              {VenueLocationMap ? (
                <VenueLocationMap
                  lat={venue.lat!}
                  lng={venue.lng!}
                  label={displayLabel}
                  className="pulse-map-container h-48 w-full overflow-hidden rounded-2xl border border-border"
                />
              ) : (
                <div className="flex h-48 items-center justify-center rounded-2xl border border-border bg-surface">
                  <LoadingBlock label={t("event.loadingMap")} />
                </div>
              )}
            </div>
          </section>
        )}

        <EntityRatingSection
          entityType="venue"
          entityId={venue.id}
          entityLabel={displayLabel}
          pulseRating={venue.pulseRating}
          pulseRatingCount={venue.pulseRatingCount}
          googleRating={venue.googleRating}
        />
      </div>

      {typeof document !== "undefined" &&
        createPortal(
          <div className="venue-detail-sticky" data-testid="venue-cta-dock">
            <button
              type="button"
              onClick={() => setProposeOpen(true)}
              className="venue-detail-sticky__primary"
            >
              ✦ {t("venue.addToSquadNight")}
            </button>
            {hasTicketTypes && (
              <button
                type="button"
                onClick={handleTicketClick}
                aria-label={t("event.buyTickets")}
                className="venue-detail-sticky__secondary"
              >
                <Ticket className="h-5 w-5" />
              </button>
            )}
          </div>,
          document.body,
        )}

      <ProposeToSquadSheet
        open={proposeOpen}
        onClose={() => setProposeOpen(false)}
        target={{
          kind: "venue",
          venueId: venue.id,
          title: displayLabel,
          defaultDate: zurichDateKey(),
        }}
      />

      {shareStatus && (
        <p className="fixed inset-x-0 bottom-24 z-40 mx-auto w-full max-w-md px-5">
          <span className="block rounded-2xl border border-pulse/30 bg-pulse/10 px-4 py-2 text-center text-xs text-pulse">
            {shareStatus}
          </span>
        </p>
      )}
    </div>
  );
}
